import * as Sentry from '@sentry/vue';

import type { PartnersProfile } from '@/models/ProfileInterface';
import { decodeJwtPayload } from '@/auth/session';

export const RAY_ID_HEADER = 'CF-Ray';
export const REQUEST_ID_HEADER = 'X-Request-Id';

type HeadersLike =
  | Headers
  | Record<string, unknown>
  | { get?: (name: string) => unknown }
  | undefined
  | null;

function readHeader(headers: HeadersLike, name: string): string | undefined {
  if (!headers) return undefined;
  if (typeof (headers as Headers).get === 'function') {
    return ((headers as Headers).get(name) as string | null) ?? undefined;
  }
  const record = headers as Record<string, unknown>;
  const value = record[name.toLowerCase()] ?? record[name];
  return value ? String(value) : undefined;
}

export function getResponseIds(headers: HeadersLike) {
  return {
    ray_id: readHeader(headers, RAY_ID_HEADER),
    request_id: readHeader(headers, REQUEST_ID_HEADER),
  };
}

type SentryProfile = Pick<
  PartnersProfile,
  | 'id'
  | 'user'
  | 'full_name'
  | 'fantasy_name'
  | 'partner_profile_id'
  | 'partner_type'
  | 'account_executive'
>;

export function setSentryUserFromProfile(profile: SentryProfile) {
  Sentry.setUser({
    id: profile.id,
    email: profile.user.email,
    username: profile.full_name || profile.fantasy_name || profile.user.email,
  });
  Sentry.setTags({
    auth_state: 'authenticated',
    partner_profile_id: profile.partner_profile_id,
    partner_type: profile.partner_type,
  });
  Sentry.setContext('partner', {
    id: profile.id,
    partner_profile_id: profile.partner_profile_id,
    fantasy_name: profile.fantasy_name,
    partner_type: profile.partner_type,
    account_executive: profile.account_executive,
  });
}

export function getEmailFromToken(token?: string): string | undefined {
  const payload = token ? decodeJwtPayload(token) : null;
  return payload?.email ?? payload?.preferred_username;
}

export function setSentryLoginEmail(email: string) {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return;
  Sentry.setUser({ email: normalized, username: normalized });
  Sentry.setTag('auth_state', 'anonymous');
}

export function clearSentryUser() {
  Sentry.setUser(null);
  Sentry.setTags({
    auth_state: 'anonymous',
    partner_profile_id: undefined,
    partner_type: undefined,
  });
  Sentry.setContext('partner', null);
}

type LoginFailureResponse = {
  status?: number;
  data?: { error?: string; error_description?: string; detail?: string };
};

export function reportLoginFailure(
  cause: unknown,
  context: { email: string; shownMessage?: string },
) {
  const response = (cause as { response?: LoginFailureResponse })?.response;
  const errorCode =
    response?.data?.error ?? response?.status?.toString() ?? 'network_error';

  const error = new Error(`Login failed: ${errorCode}`);
  error.name = 'LoginFailure';

  Sentry.captureException(error, {
    level: 'info',
    fingerprint: ['login-failure', errorCode],
    user: { email: context.email },
    tags: {
      flow: 'login',
      auth_error: errorCode,
      expected_error: String(['invalid_grant'].includes(errorCode)),
    },
    contexts: {
      auth: {
        flow: 'login',
        status: response?.status,
        response: response?.data,
        error: response?.data?.error,
        error_description: response?.data?.error_description,
        detail: response?.data?.detail,
        shown_message: context.shownMessage,
        original_error: cause instanceof Error ? cause.message : undefined,
      },
    },
  });
}

type HttpErrorReport = {
  source: 'axios' | 'graphql';
  message: string;
  method?: string;
  url?: string;
  status?: number;
  headers?: HeadersLike;
  operationName?: string;
  responseData?: unknown;
  level?: Sentry.SeverityLevel;
  fingerprint?: string[];
  cause?: unknown;
  callSite?: string;
};

const STACK_FRAME = /^\s*at |@/;

export function captureCallSite(
  caller: (...args: never[]) => unknown = captureCallSite,
): string | undefined {
  const trace: { stack?: string } = {};
  if (Error.captureStackTrace) Error.captureStackTrace(trace, caller);
  else trace.stack = new Error().stack;

  const frames = trace.stack
    ?.split('\n')
    .filter(line => STACK_FRAME.test(line));
  if (!Error.captureStackTrace) frames?.shift();
  return frames?.join('\n') || undefined;
}

const MAX_CLICKED_TEXT_LENGTH = 60;

export function getClickedText(target: unknown): string | undefined {
  if (!(target instanceof Element)) return undefined;
  const element =
    target.closest(
      'button, a, [role="button"], [role="option"], [role="tab"], [role="menuitem"], label',
    ) ?? target;
  const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return undefined;
  return text.length > MAX_CLICKED_TEXT_LENGTH
    ? `${text.slice(0, MAX_CLICKED_TEXT_LENGTH)}…`
    : text;
}

export const normalizePath = (url = '') =>
  url
    .split('?')[0]
    .replace(/\/(\d+|[0-9a-f]{8}-[0-9a-f-]{27,})(?=\/|$)/gi, '/:id');

const MAX_RESPONSE_LENGTH = 4000;

function truncateResponse(data: unknown) {
  if (data === undefined) return undefined;
  const serialized = typeof data === 'string' ? data : JSON.stringify(data);
  return serialized && serialized.length > MAX_RESPONSE_LENGTH
    ? `${serialized.slice(0, MAX_RESPONSE_LENGTH)}…`
    : serialized;
}

export function reportHttpError(report: HttpErrorReport) {
  const { ray_id, request_id } = getResponseIds(report.headers);

  const error = new Error(report.message);
  error.name = report.source === 'graphql' ? 'GraphQLError' : 'HttpError';
  if (report.callSite) {
    error.stack = `${error.name}: ${error.message}\n${report.callSite}`;
  }
  const cause = report.cause instanceof Error ? report.cause : undefined;

  // eslint-disable-next-line no-console
  console.error(
    `[${report.source}] ${report.message} (status: ${
      report.status ?? '-'
    }, ray_id: ${ray_id ?? '-'}, request_id: ${request_id ?? '-'})`,
  );

  Sentry.captureException(error, {
    level: report.level ?? 'error',
    fingerprint: report.fingerprint,
    tags: {
      error_source: report.source,
      http_status: report.status?.toString() ?? 'network_error',
      graphql_operation: report.operationName,
      ray_id,
      request_id,
    },
    contexts: {
      http_request: {
        method: report.method,
        url: report.url,
        status: report.status,
        operation_name: report.operationName,
        ray_id,
        request_id,
        response: truncateResponse(report.responseData),
        original_error: cause && `${cause.name}: ${cause.message}`,
      },
    },
  });
}
